import React, { useState, useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import queryString from "query-string";

type RouteInfo = {
  distance: number | null;
  duration: number | null;
};

type ShowSuggestPanel = {
  top: number;
  left: number;
  height: number;
} | null;

interface IContext {
  loading: boolean;
  setLoading: (input: boolean) => void;
  findAlternatives: boolean;
  setFindAlternatives: (input: boolean) => void;
  travelType: string;
  setTravelType: (input: string) => void;
  animate: boolean;
  setAnimate: (input: boolean) => void;
  routeInfo: RouteInfo;
  setRouteInfo: (input: RouteInfo) => void;
  markers: [number, number][];
  setMarkers: (input: [number, number][]) => void;
  fromInput: string;
  setFromInput: (input: string) => void;
  toInput: string;
  setToInput: (input: string) => void;
  showSuggestPanel: ShowSuggestPanel;
  setShowSuggestPanel: (input: ShowSuggestPanel) => void;
}

export const MapContext = React.createContext<Partial<IContext>>({});

export const MapContextProvider = ({
  children,
}: {
  children: React.ReactNode[];
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [findAlternatives, setFindAlternatives] = useState(false);
  const [animate, setAnimate] = useState(false);
  const [travelType, setTravelType] = useState("car");
  const [routeInfo, setRouteInfo] = useState<RouteInfo>({
    distance: null,
    duration: null,
  });
  const [markers, setMarkers] = useState<[number, number][]>([]);
  const [fromInput, setFromInput] = useState<string>("");
  const [toInput, setToInput] = useState<string>("");
  const history = useHistory();
  const location = useLocation();
  const isFirstRender = useRef(true);
  const [showSuggestPanel, setShowSuggestPanel] = useState<ShowSuggestPanel>(
    null
  );

  useEffect(() => {
    if (isFirstRender.current) {
      const search = history.location.search;
      const parsed: any = queryString.parse(search, {
        decode: true,
        arrayFormat: "comma",
        parseNumbers: true,
      });
      // console.log(parsed);

      parsed.alternative &&
        setFindAlternatives!(parsed.alternative === "true" ? true : false);
      parsed.type && setTravelType!(parsed.type);

      let newMarkers = [];
      parsed.from && (newMarkers[0] = parsed.from);
      parsed.to && (newMarkers[1] = parsed.to);
      newMarkers.length > 0 && setMarkers(newMarkers);
    } else {
      const search = history.location.search;
      const parsed: any = queryString.parse(search, {
        decode: true,
        arrayFormat: "comma",
        parseNumbers: true,
      });

      if (markers!.length < 1 && !findAlternatives && travelType === "car")
        return;

      parsed.alternative = findAlternatives;
      parsed.type = travelType;
      markers!.length > 0 && markers![0] && (parsed.from = `${markers![0]}`);
      markers!.length === 2 && (parsed.to = `${markers![1]}`);

      const newQueryString = `?${queryString.stringify(parsed, {
        arrayFormat: "comma",
        encode: false,
      })}`;

      if (search !== newQueryString) {
        history.push({
          pathname: "/",
          search: newQueryString,
        });
      }
    }
  }, [findAlternatives, history, markers, travelType]);

  useEffect(() => {
    if (!isFirstRender.current && history.action === "POP") {
      const search = location.search;
      const parsed: any = queryString.parse(search, {
        decode: true,
        arrayFormat: "comma",
        parseNumbers: true,
      });

      parsed.type && setTravelType!(parsed.type);
      parsed.alternative &&
        setFindAlternatives!(parsed.alternative === "true" ? true : false);

      let newMarkers = [];
      if (parsed.from) newMarkers[0] = parsed.from;
      else setFromInput("");

      if (parsed.to) newMarkers[1] = parsed.to;
      else setToInput("");

      if (newMarkers.length < 2)
        setRouteInfo({ distance: null, duration: null });

      setMarkers(newMarkers);
    }

    isFirstRender.current = false;
  }, [history.action, location]);

  return (
    <MapContext.Provider
      value={{
        loading,
        setLoading,
        findAlternatives,
        setFindAlternatives,
        animate,
        setAnimate,
        travelType,
        setTravelType,
        routeInfo,
        setRouteInfo,
        markers,
        setMarkers,
        fromInput,
        setFromInput,
        toInput,
        setToInput,
        showSuggestPanel,
        setShowSuggestPanel,
      }}
    >
      {children}
    </MapContext.Provider>
  );
};

export default React.memo(MapContextProvider);
